import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { Wrench, Phone, Truck, Snowflake, Gauge, Cpu, CircleDot, ShieldCheck, MapPin, ArrowRight, CheckCircle2, Clock } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { ReviewForm } from '../components/ReviewForm';
import { LocalReviews } from '../components/LocalReviews';
import { REGIONS, ORDER } from '../locationsData';
import { CONTACT } from '../lyphline';

const SYMPTOMS = [
  { icon: Gauge, t: "Won't pull or hold vacuum", d: 'Vacuum pump service or replacement, leak checks and seal replacement.' },
  { icon: Snowflake, t: 'Not freezing or cooling', d: 'Refrigeration diagnostics, condenser and compressor rebuilds.' },
  { icon: Cpu, t: 'Error codes or blank display', d: 'Control board, wiring and sensor testing, repair and calibration.' },
  { icon: CircleDot, t: 'Leaking or product still wet', d: 'Gasket and seal replacement plus full cycle testing.' },
];

const LocationLanding = () => {
  const { region } = useParams();
  const r = REGIONS[region];
  if (!r) return <Navigate to="/freeze-dryer-repair/alberta" replace />;

  const title = `Freeze Dryer Repair in ${r.name}`;
  const metaDescription = `Freeze dryer repair and rebuilds for ${r.name}. Fix a freeze dryer that won't pull vacuum, won't freeze or throws error codes. Mail-in rebuilds with a 90-day rebuild warranty, ${r.transit} transit. Free diagnostic quote.`;
  const keywords = `freeze dryer repair ${r.name}, freeze dryer rebuild ${r.name}, freeze dryer service ${r.name}, freeze dryer vacuum pump replacement, freeze dryer won't freeze`;

  const laneNote = r.tier === 'closest'
    ? `${r.name} is close to our shop — one of the fastest, most affordable shipping lanes.`
    : r.tier === 'cross'
      ? `We serve customers across ${r.name}. Cross-border shipments are straightforward and we confirm any duties with your quote.`
      : `${r.name} ships to our shop in about ${r.transit}, and we handle the return once your rebuild is complete.`;
  const heroBlurb = r.blurb ? ` ${r.blurb}` : '';
  const nearby = ORDER.filter((k) => k !== region && REGIONS[k].country === r.country).slice(0, 12);

  const faqs = [
    { q: `Do you repair freeze dryers in ${r.name}?`, a: `Yes. We serve ${r.name} (${r.hubs}) with mail-in freeze dryer rebuilds. You ship us your machine, we diagnose, rebuild and bench-test it, then ship it back running — typically ${r.transit} each way.` },
    { q: `How does mail-in freeze dryer repair work in ${r.name}?`, a: `Start with a free diagnostic quote, pack your unit (we can send a prepaid label), and ship it to our shop. We send a written quote before any work, complete the rebuild, and return it with a 90-day rebuild warranty.` },
    { q: `Which freeze dryer brands do you service?`, a: `We're an independent rebuilder and service most major freeze dryer brands of select makes and models. Send your machine details and we'll confirm whether yours is a fit.` },
    { q: `What are the most common freeze dryer repairs?`, a: `Vacuum pump replacement, refrigeration and condenser rebuilds, control board and sensor repair, and door gasket replacement for machines that won't pull vacuum, won't freeze, throw error codes or leave product wet.` },
  ];

  return (
    <div className="min-h-screen bg-white" data-testid="location-landing">
      <SEO title={title} description={metaDescription} keywords={keywords} faq={faqs} />
      <Navbar />

      <section className="relative bg-[#003456] pt-16 pb-20 section-padding overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <span className="inline-flex items-center gap-2 text-[#CE8946] font-semibold text-xs uppercase tracking-[0.2em]">
            <MapPin className="h-4 w-4" /> Serving {r.name}
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mt-4 leading-tight max-w-3xl">
            Freeze Dryer Repair &amp; Rebuilds in {r.name}
          </h1>
          <p className="text-white/70 mt-5 max-w-2xl leading-relaxed">
            Is your freeze dryer down? LyphLine rebuilds freeze dryers for owners across {r.name} — from home units to
            semi-commercial machines. Ship it to our shop and we'll bring it back to dependable performance, backed by a
            90-day rebuild warranty.{heroBlurb} {laneNote}
          </p>
          {r.type === 'city' && r.province && (
            <p className="text-white/50 text-sm mt-3">
              Serving the wider region?{' '}
              <Link to={`/freeze-dryer-repair/${r.province}`} className="text-[#CE8946] font-semibold hover:underline" data-testid="location-parent-link">
                Freeze dryer repair in {r.provinceName}
              </Link>
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Link to="/repair/quote" className="inline-flex items-center justify-center gap-2 bg-[#CE8946] hover:bg-[#B87333] text-[#003456] font-bold px-7 py-3.5 rounded-md transition-colors" data-testid="location-quote-cta">
              <Wrench className="h-5 w-5" /> Get my free quote
            </Link>
            <a href={CONTACT.phoneHref} className="inline-flex items-center justify-center gap-2 border border-white/25 text-white font-semibold px-7 py-3.5 rounded-md hover:bg-white/10 transition-colors">
              <Phone className="h-5 w-5" /> {CONTACT.phone}
            </a>
          </div>
          <div className="flex flex-wrap gap-x-8 gap-y-3 mt-8 text-white/60 text-sm">
            <span className="inline-flex items-center gap-2"><Truck className="h-4 w-4 text-[#CE8946]" /> {r.transit} transit</span>
            <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-[#CE8946]" /> 90-day rebuild warranty</span>
            <span className="inline-flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-[#CE8946]" /> Most major brands</span>
          </div>
        </div>
      </section>

      <section className="py-20 section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-10">
            <span className="text-[#B87333] font-semibold text-xs uppercase tracking-[0.2em]">Problems we fix</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#003456] mt-3">Common freeze dryer repairs in {r.name}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SYMPTOMS.map((s) => (
              <div key={s.t} className="bg-[#F1F5F9] rounded-2xl p-6 border border-slate-200">
                <s.icon className="h-6 w-6 text-[#CE8946] mb-3" />
                <h3 className="text-base font-bold text-[#003456]">{s.t}</h3>
                <p className="text-slate-600 text-sm mt-1.5 leading-snug">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 section-padding bg-[#F1F5F9]">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-2xl mb-10">
            <span className="text-[#B87333] font-semibold text-xs uppercase tracking-[0.2em]">Mail-in rebuilds</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#003456] mt-3">How freeze dryer repair works from {r.name}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { icon: Wrench, t: 'Free diagnostic quote', d: "Tell us what's happening. We assess and send a firm written quote." },
              { icon: Truck, t: 'Ship it to our shop', d: `We can email a prepaid label. Transit runs about ${r.transit}.` },
              { icon: Gauge, t: 'Rebuild & test', d: 'We rebuild with quality parts and run a full cycle test.' },
              { icon: ShieldCheck, t: 'Back to you, warrantied', d: 'Returned running and backed by a 90-day rebuild warranty.' },
            ].map((s, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-slate-200">
                <div className="w-10 h-10 rounded-lg bg-[#CE8946]/15 flex items-center justify-center mb-3">
                  <s.icon className="h-5 w-5 text-[#B87333]" />
                </div>
                <h3 className="text-base font-bold text-[#003456]">{s.t}</h3>
                <p className="text-slate-600 text-sm mt-1.5 leading-snug">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#003456] mb-8">Freeze dryers we rebuild for {r.name}</h2>
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              { slug: 'residential', t: 'Residential & home units' },
              { slug: 'small-business', t: 'Small business machines' },
              { slug: 'semi-commercial', t: 'Semi-commercial & production' },
            ].map((mc) => (
              <Link key={mc.slug} to={`/machines/${mc.slug}`} className="group bg-[#F1F5F9] rounded-2xl p-6 border border-slate-200 hover:border-[#CE8946] transition-colors flex items-center justify-between">
                <span className="font-bold text-[#003456]">{mc.t}</span>
                <ArrowRight className="h-5 w-5 text-[#CE8946] group-hover:translate-x-1 transition-transform" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 section-padding bg-[#F1F5F9]">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-[#003456] mb-6">Freeze dryer repair in {r.name} — FAQs</h2>
          <div className="space-y-4">
            {faqs.map((f, i) => (
              <div key={i} className="bg-white rounded-xl border border-slate-200 p-5">
                <h3 className="font-semibold text-[#003456]">{f.q}</h3>
                <p className="text-slate-600 text-sm mt-2 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Approved local reviews for this place */}
      <LocalReviews subject={`location:${r.name}`} placeName={r.name} testid="location-local-reviews" />

      {/* Review capture */}
      <ReviewForm subject={`location:${r.name}`} testid="location-review-form" />

      <section className="py-14 section-padding bg-white border-t border-slate-100">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-2 mb-4">
            <Clock className="h-4 w-4 text-[#CE8946]" />
            <span className="text-sm text-slate-500 font-medium">Also serving</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {nearby.map((k) => (
              <Link key={k} to={`/freeze-dryer-repair/${k}`} className="text-sm px-4 py-2 rounded-full bg-[#F1F5F9] border border-slate-200 text-[#003456] hover:border-[#CE8946] transition-colors" data-testid={`location-link-${k}`}>
                Freeze dryer repair in {REGIONS[k].name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default LocationLanding;
